import { useSelector } from 'react-redux';
import { enumEquals } from 'helpers/utils';
import { VerticalEnum } from 'models/vertical-enum';
import { useModalControl } from '@weave/design-system';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LocationModel, LocationTypes } from 'redux/actions/location';
import {
  CustomizationFlagStatesV2,
  LocationFeatureV2,
} from 'models/location-feature.model';
import { selectFeatures } from 'redux/actions/customization/customization.action';
import {
  customizationFlagsQueryKeys,
  useFetchCustomizationsFlagByBundles,
} from '../customization-page.queries';
import { CustomizationFlagEditModal } from './components/customization-flag-edit-modal';
import { useWeaveVerifyPermittedACL } from 'components/weave-verify/weave-verify-utils.hooks';
import { ToggleState } from 'components/shared/multi-state-toggle/multi-state-toggle.component';
import { WeaveVerifyEnableCustomizationFlagModal } from 'components/weave-verify/weave-verify-enable-customization-flag-modal';
import { LocationFeatureStates } from './customization-page-v2.constant';
import {
  CustomizationFlagV2ContextType,
  CustomizationFlagV2Provider,
  CustomizationFlagCountryCodeVerifiedProvider,
  CustomizationProhibitedFlagsProvider,
  CustomizationFlagBundleFlagProvider,
} from './providers';
import {
  ModalData,
  WeaveVerifyCustomizationFlagModal,
} from 'components/weave-verify/weave-verify-customization-flag-modal';
import { CustomizationPageContent } from './customization-page-content';
import { CustomizationFlagTableFilterProvider } from './providers/customization-flag-table-filter-provider';
import { isDevEnv } from '../../../config/app';
import { WamSyncStatusResponse } from 'apis/wam-sync/wam-sync.types';
import { selectFeatureFlag } from '../../../redux/actions/feature-flags';
import { WamSyncFeatureFlags } from '../customization.constant';
import { useQueryClient } from 'react-query';
import {
  checkIsWeaveInsuranceVerificationFlag,
  getCustomizationFlagStateDetails,
  isQuotaEnabledForFeature,
} from './customization-page.utils';

interface Props {
  loading: boolean;
  canEdit: boolean;
  features: LocationFeatureV2[];
  verticalId: VerticalEnum;
  location: LocationModel;
  initialFeatures: LocationFeatureV2[];
  wamSyncStatus: WamSyncStatusResponse;
  getLocationFeatures: (locationId: string) => void;
  getWamSyncStatus: (locationSlug: string) => void;
  setLocationFeatures: (
    locationId: string,
    features: LocationFeatureV2[],
    updatedFeatures: LocationFeatureV2[],
    locationSlug?: string
  ) => void;
  updateFeature: (feature: LocationFeatureV2, newState: ToggleState) => void;
  setFeatures: (features: LocationFeatureV2[]) => void;
  showError: (message: string) => void;
}

const isDev = isDevEnv();

export const CustomizationPageV2 = ({
  canEdit,
  loading,
  features,
  location,
  getLocationFeatures,
  getWamSyncStatus,
  verticalId,
  updateFeature,
  setLocationFeatures,
  initialFeatures,
  setFeatures,
  showError,
  wamSyncStatus,
}: Props) => {
  const hasWeaveVerifyACL = useWeaveVerifyPermittedACL();
  const modalData = useRef<ModalData>({} as ModalData);
  const customizationFlagsData = useSelector(selectFeatures);
  const [tableData, setTableData] = useState<LocationFeatureV2[]>(customizationFlagsData);
  const [isCustomizationFlagEdited, setIsCustomizationFlagEdited] =
    useState<boolean>(false);

  const qc = useQueryClient();

  const preventSyncedEditFF = useSelector((state) =>
    selectFeatureFlag(state, WamSyncFeatureFlags.PREVENT_SYNCED_CUSTOMIZATION_CHANGE)
  );

  const customizationFlagEditModalProps = useModalControl();

  const featuresOutOfSyncMap = useMemo<Record<string, boolean>>(() => {
    return (wamSyncStatus?.customizationDetails || []).reduce((acc, curr) => {
      acc[curr.name] = true;
      return acc;
    }, {});
  }, [wamSyncStatus?.customizationDetails]);

  const isDemoLocation =
    location?.Type === LocationTypes.Demo || location?.Type?.toString() === 'Demo';
  const isDevLocation =
    location?.Type === LocationTypes.Dev || location?.Type?.toString() === 'Dev';

  const canEditCustomizationFlag = canEdit || isDev || isDemoLocation || isDevLocation;

  const hasOutOfSyncData = !!wamSyncStatus?.customizationDetails;

  const preventSyncedFeaturesChange =
    hasOutOfSyncData && !!preventSyncedEditFF && !(isDemoLocation || isDevLocation);

  const disableBulkEditCustomization =
    preventSyncedFeaturesChange && wamSyncStatus?.customizationDetails?.length === 0;

  const {
    modalProps: { show: showEditCustomizationFlagModal },
  } = customizationFlagEditModalProps;

  const {
    modalProps: disableInsuranceVerificationModalProps,
    openModal: openDisableInsuranceVerificationModal,
    closeModal: closeDisableInsuranceVerificationModal,
  } = useModalControl();

  const {
    modalProps: enableInsuranceVerificationModalProps,
    openModal: openEnableInsuranceVerificationModal,
    closeModal: closeEnableInsuranceVerificationModal,
  } = useModalControl();

  const {
    mutateAsync: getCustomizationsFlagsByBundles,
    isLoading: isGetCustomizationsFlagsByBundlesLoading,
  } = useFetchCustomizationsFlagByBundles();

  const refetchCustomizationFlag = () => {
    getLocationFeatures(location?.LocationID);
    getWamSyncStatus(location?.Slug);
  };

  const saveCustomizationFlags = () => {
    if (canEditCustomizationFlag) {
      const updatedFeatures = features
        .filter(
          (feature, index) =>
            feature.state !== initialFeatures[index].state ||
            feature.isFreeTrial !== initialFeatures[index].isFreeTrial ||
            feature.quota !== initialFeatures[index].quota
        )
        .map((feature) => {
          // If the feature is hidden and quota is enabled, set quota to 0
          if (
            isQuotaEnabledForFeature(feature) &&
            feature.state === CustomizationFlagStatesV2.HIDE
          ) {
            feature.quota = '0';
          }
          return feature;
        });

      qc.invalidateQueries({
        predicate: (query) => {
          return (
            query.queryKey.includes(location.LocationID) &&
            customizationFlagsQueryKeys.base.every((part) =>
              query.queryKey.includes(part)
            )
          );
        },
      });

      setLocationFeatures(location.LocationID, features, updatedFeatures, location.Slug);
    }
  };

  const cancelSavingCustomizationFlags = () => {
    setFeatures(initialFeatures);
    setIsCustomizationFlagEdited(false);
  };

  const updateSelectedFeature = (
    featureToToggle: LocationFeatureV2,
    newState: ToggleState
  ) => {
    updateFeature(featureToToggle, newState);
  };

  const checkIsLocationTypeRegular = (): boolean => {
    return enumEquals(location.Type, LocationTypes.Regular, LocationTypes);
  };

  const getCustomizationFlagDetails = (
    currentState: CustomizationFlagStatesV2,
    newState: ToggleState
  ) => {
    const isRegularType = checkIsLocationTypeRegular();
    const { isHideState, isFlagChanged } = getCustomizationFlagStateDetails(
      currentState,
      newState
    );

    return {
      isRegularType,
      isHideState,
      isFlagChanged,
    };
  };

  const handleInsuranceCFToggle = (
    featureToToggle: LocationFeatureV2,
    newState: ToggleState
  ) => {
    const { isRegularType, isHideState, isFlagChanged } = getCustomizationFlagDetails(
      featureToToggle.state,
      newState
    );

    if (!checkIsWeaveInsuranceVerificationFlag(featureToToggle)) {
      return false;
    } else if (!isRegularType || !isFlagChanged) {
      return false;
    } else if (!hasWeaveVerifyACL) {
      showError(
        'Action not allowed! Only Level 2 Support and Multi office admin are allowed to perform the action'
      );
      return true;
    } else {
      // updating the state to Active/Show if the state is promote/freemium for Weave Verify
      // Weave Verify flag will only have two state Show/Active or Hide
      const showState = [
        CustomizationFlagStatesV2.PROMOTE,
        CustomizationFlagStatesV2.FREEMIUM,
      ].includes(newState.key as CustomizationFlagStatesV2)
        ? (LocationFeatureStates.find(
            (state) => state.key === CustomizationFlagStatesV2.ACTIVE
          ) as ToggleState)
        : newState;
      modalData.current = {
        featureToToggle,
        newState: showState,
      };

      isHideState
        ? openDisableInsuranceVerificationModal()
        : openEnableInsuranceVerificationModal();

      return true;
    }
  };

  // Cycle to the next state in the list of possible states
  const toggleCustomizationFlag = (
    featureToToggle: LocationFeatureV2,
    newState: ToggleState
  ) => {
    if (!canEditCustomizationFlag) {
      return;
    }

    // For Regular accounts, users must self sign up with for weave verify and weave verify beta
    if (
      checkIsWeaveInsuranceVerificationFlag(featureToToggle) &&
      handleInsuranceCFToggle(featureToToggle, newState)
    ) {
      return;
    }

    updateFeature(featureToToggle, newState);
  };

  const updateFeatureQuota = (feature: LocationFeatureV2, quota: number) => {
    if (!canEditCustomizationFlag) {
      return;
    }
    updateFeature(feature, {
      key: feature.state,
      label: feature.state,
      quota: `${quota}`,
    });
  };

  //useEffect to check whether customization flags are changed / edited
  useEffect(() => {
    const isCustomizationFlagAvailable = features.length;
    const isInitialCustomizationFlagAvailable = initialFeatures.length;

    if (isCustomizationFlagAvailable && isInitialCustomizationFlagAvailable) {
      const isCustomizationFlagChanged =
        features.filter(
          (feature, index) =>
            feature.state !== initialFeatures[index].state ||
            feature.isFreeTrial !== initialFeatures[index].isFreeTrial ||
            feature.quota !== initialFeatures[index].quota
        )?.length > 0;
      if (isCustomizationFlagChanged) {
        setIsCustomizationFlagEdited(true);
      } else {
        setIsCustomizationFlagEdited(false);
      }
    }
  }, [features, initialFeatures]);

  useEffect(() => {
    if (location?.LocationID) {
      getLocationFeatures(location?.LocationID);
    }
  }, [location?.LocationID]);

  const customizationFlagProviderValue = useMemo<CustomizationFlagV2ContextType>(() => {
    return {
      loading,
      cancelSavingCustomizationFlags,
      isCustomizationFlagEdited,
      saveCustomizationFlags,
      verticalId,
      refetchCustomizationFlag,
      customizationFlagEditModalProps,
      getCustomizationsFlagsByBundles,
      isGetCustomizationsFlagsByBundlesLoading,
      tableData,
      setTableData,
      toggleCustomizationFlag,
      canEdit: canEditCustomizationFlag,
      initialFeatures,
      updateFeatureQuota,
      featuresOutOfSyncMap,
      preventSyncedFeaturesChange,
      disableBulkEditCustomization,
    };
  }, [
    loading,
    cancelSavingCustomizationFlags,
    isCustomizationFlagEdited,
    saveCustomizationFlags,
    verticalId,
    refetchCustomizationFlag,
    customizationFlagEditModalProps,
    canEditCustomizationFlag,
    getCustomizationsFlagsByBundles,
    isGetCustomizationsFlagsByBundlesLoading,
    tableData,
    setTableData,
    toggleCustomizationFlag,
    initialFeatures,
    updateFeatureQuota,
    featuresOutOfSyncMap,
    preventSyncedFeaturesChange,
    disableBulkEditCustomization,
  ]);

  return (
    <CustomizationFlagV2Provider value={customizationFlagProviderValue}>
      <CustomizationFlagCountryCodeVerifiedProvider>
        <CustomizationProhibitedFlagsProvider>
          <CustomizationFlagTableFilterProvider>
            <CustomizationFlagBundleFlagProvider>
              <CustomizationPageContent />
              {verticalId && showEditCustomizationFlagModal && (
                <CustomizationFlagEditModal />
              )}
              {disableInsuranceVerificationModalProps.show && (
                <WeaveVerifyCustomizationFlagModal
                  closeModal={closeDisableInsuranceVerificationModal}
                  onAccept={updateSelectedFeature}
                  modalData={modalData.current}
                  {...disableInsuranceVerificationModalProps}
                />
              )}
              {enableInsuranceVerificationModalProps.show && (
                <WeaveVerifyEnableCustomizationFlagModal
                  closeModal={closeEnableInsuranceVerificationModal}
                  onAccept={updateSelectedFeature}
                  modalData={modalData.current}
                  {...enableInsuranceVerificationModalProps}
                />
              )}
            </CustomizationFlagBundleFlagProvider>
          </CustomizationFlagTableFilterProvider>
        </CustomizationProhibitedFlagsProvider>
      </CustomizationFlagCountryCodeVerifiedProvider>
    </CustomizationFlagV2Provider>
  );
};
